import React, { useState, useEffect } from 'react';
import './ABCCalculators.css';

const ABCCalculators = () => {
  const [selectedCategory, setSelectedCategory] = useState('Insurance');
  const [activeCalculator, setActiveCalculator] = useState('HLV Calculator');

  // Loans State
  const [loanType, setLoanType] = useState('personal');
  const [tenure, setTenure] = useState(1);
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(9);

  // HLV State
  const [currentAge, setCurrentAge] = useState(18);
  const [annualIncome, setAnnualIncome] = useState(500000);
  const [totalLiabilities, setTotalLiabilities] = useState(0);
  const [existingCover, setExistingCover] = useState(0);

  // BMI State
  const [weight, setWeight] = useState(70); // always stored in kg
  const [height, setHeight] = useState(170); // always stored in cm
  const [weightUnit, setWeightUnit] = useState('kg'); // 'kg' or 'lbs'
  const [heightUnit, setHeightUnit] = useState('cm'); // 'cm' or 'ft'

  // Calculator Options
  const calculators = {
    Loans: [
      { name: 'Home Loan EMI Calculator' },
      { name: 'Home Loan Eligibility Calculator' },
      { name: 'Personal Loan EMI Calculator' }
    ],
    Insurance: [
      { name: 'HLV Calculator' },
      { name: 'BMI Calculator' }
    ]
  };

  // Update active calculator when category changes
  useEffect(() => {
    if (selectedCategory === 'Insurance') {
      setActiveCalculator('HLV Calculator');
    } else {
      setActiveCalculator('Home Loan EMI Calculator');
    }
  }, [selectedCategory]);

  // Calculations
  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate) / 12 / 100;
    const time = parseFloat(tenure) * 12;

    if (rate === 0) return { emi: Math.round(principal / time), totalInterest: 0, totalPayout: principal };

    const emi = (principal * rate * Math.pow(1 + rate, time)) / (Math.pow(1 + rate, time) - 1);
    const totalPayout = emi * time;
    const totalInterest = totalPayout - principal;

    return {
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalPayout: Math.round(totalPayout)
    };
  };

  const calculateHLV = () => {
    const retirementAge = 60;
    const yearsLeft = Math.max(0, retirementAge - currentAge);
    const hlv = (yearsLeft * annualIncome) - totalLiabilities - existingCover;
    return Math.max(0, hlv);
  };

  const calculateBMIAdvanced = () => {
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);

    // Categories
    let category = "Normal";
    let color = "#8bc34a";
    if (bmi < 18.5) { category = "Underweight"; color = "#90caf9"; }
    else if (bmi >= 18.5 && bmi < 25) { category = "Normal"; color = "#8bc34a"; }
    else if (bmi >= 25 && bmi < 30) { category = "Overweight"; color = "#ff9800"; }
    else if (bmi >= 30 && bmi < 40) { category = "Obese"; color = "#f44336"; }
    else if (bmi >= 40) { category = "Severely Obese"; color = "#b71c1c"; }

    // Advanced Metrics
    // Healthy weight range: BMI 18.5 to 25
    const minHealthyWeight = 18.5 * heightInMeters * heightInMeters;
    const maxHealthyWeight = 25 * heightInMeters * heightInMeters;

    // Weight to lose/gain
    let weightChangeMsg = "You are at a healthy weight.";
    if (bmi > 25) {
      const lose = weight - maxHealthyWeight;
      weightChangeMsg = `Lose ${lose.toFixed(1)} Kgs To Reach A BMI Of 25 Kg/M2.`;
    } else if (bmi < 18.5) {
      const gain = minHealthyWeight - weight;
      weightChangeMsg = `Gain ${gain.toFixed(1)} Kgs To Reach A BMI Of 18.5 Kg/M2.`;
    }

    // BMI Prime: BMI / 25
    const bmiPrime = bmi / 25;

    // Ponderal Index: weight / height^3
    const ponderalIndex = weight / (heightInMeters * heightInMeters * heightInMeters);

    return {
      bmi: isNaN(bmi) ? 0 : bmi.toFixed(1),
      category,
      color,
      minHealthyWeight: minHealthyWeight.toFixed(1),
      maxHealthyWeight: maxHealthyWeight.toFixed(1),
      weightChangeMsg,
      bmiPrime: bmiPrime.toFixed(2),
      ponderalIndex: ponderalIndex.toFixed(1)
    };
  };

  const emiResults = calculateEMI();
  const interestPercentage = (emiResults.totalInterest / emiResults.totalPayout) * 100;
  const hlvResult = calculateHLV();
  const bmiDetails = calculateBMIAdvanced();

  // Helper for Height Display (Feet + Inches)
  const formatHeight = () => {
    if (heightUnit === 'cm') return `${Math.round(height)} CM`;
    const totalInches = height / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet} FEET ${inches} INCHES`;
  };

  // Helper for Weight Display
  const formatWeight = () => {
    if (weightUnit === 'kg') return `${Math.round(weight)} KG`;
    return `${Math.round(weight * 2.20462)} LBS`;
  };

  return (
    <section className="calculators-section" id="calculators">
      <div className="calculators-container">
        <div className="calculators-content">
          {/* Left Sidebar */}
          <div className="calculators-sidebar">
            <h2 className="calculators-title">
              ABC Of<br /><span style={{ color: '#c8102e' }}>Calculators</span>
            </h2>

            <div className="category-dropdown">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                <option value="Loans">Loans</option>
                <option value="Insurance">Insurance</option>
              </select>
            </div>

            <div className="calculator-list">
              {calculators[selectedCategory].map((calc, index) => (
                <div
                  key={index}
                  className={`calculator-item ${activeCalculator === calc.name ? 'active' : ''}`}
                  onClick={() => setActiveCalculator(calc.name)}
                >
                  {calc.name}
                  {activeCalculator === calc.name && <span className="arrow">›</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Calculator Main Area */}
          <div className="calculator-main">
            {selectedCategory === 'Loans' && (
              <div className="loan-calculator-wrapper">
                <div className="loan-type-selector">
                  <label className="radio-option">
                    <input type="radio" name="loanType" value="personal" checked={loanType === 'personal'} onChange={(e) => setLoanType(e.target.value)} />
                    <span className="radio-label">Personal Loans</span>
                  </label>
                  <label className="radio-option">
                    <input type="radio" name="loanType" value="home" checked={loanType === 'home'} onChange={(e) => setLoanType(e.target.value)} />
                    <span className="radio-label">Home Loans</span>
                  </label>
                </div>

                <div className="calculator-wrapper-flex">
                  <div className="calculator-inputs">
                    <div className="input-group">
                      <label className="input-label">TENURE (YEARS)</label>
                      <div className="input-with-slider">
                        <input type="number" value={tenure} onChange={(e) => setTenure(Number(e.target.value))} className="input-field" />
                        <input type="range" value={tenure} onChange={(e) => setTenure(Number(e.target.value))} min="1" max="30" className="slider" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">LOAN AMOUNT</label>
                      <div className="input-with-slider">
                        <input type="number" value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} className="input-field" />
                        <input type="range" value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} min="10000" max="10000000" step="10000" className="slider" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">INTEREST RATE (%)</label>
                      <div className="input-with-slider">
                        <input type="number" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} className="input-field" />
                        <input type="range" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} min="1" max="20" step="0.1" className="slider" />
                      </div>
                    </div>
                  </div>

                  <div className="calculator-results">
                    <div className="emi-result">
                      <div className="emi-label">Your EMI</div>
                      <div className="emi-amount">₹ {emiResults.emi.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedCategory === 'Insurance' && activeCalculator === 'HLV Calculator' && (
              <div className="hlv-calculator-wrapper">
                <div className="calculator-wrapper-flex">
                  <div className="calculator-inputs">
                    <div className="input-group">
                      <label className="input-label">CURRENT AGE</label>
                      <div className="input-with-slider">
                        <input type="number" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} className="input-field" />
                        <input type="range" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} min="18" max="60" className="slider" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">ANNUAL INCOME</label>
                      <div className="input-with-slider">
                        <input type="number" value={annualIncome} onChange={(e) => setAnnualIncome(Number(e.target.value))} className="input-field" />
                        <input type="range" value={annualIncome} onChange={(e) => setAnnualIncome(Number(e.target.value))} min="100000" max="10000000" step="50000" className="slider" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">TOTAL LIABILITIES</label>
                      <div className="input-with-slider">
                        <input type="number" value={totalLiabilities} onChange={(e) => setTotalLiabilities(Number(e.target.value))} className="input-field" />
                        <input type="range" value={totalLiabilities} onChange={(e) => setTotalLiabilities(Number(e.target.value))} min="0" max="5000000" step="10000" className="slider" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label className="input-label">EXISTING COVER</label>
                      <div className="input-with-slider">
                        <input type="number" value={existingCover} onChange={(e) => setExistingCover(Number(e.target.value))} className="input-field" />
                        <input type="range" value={existingCover} onChange={(e) => setExistingCover(Number(e.target.value))} min="0" max="5000000" step="10000" className="slider" />
                      </div>
                    </div>
                  </div>

                  <div className="calculator-results hlv-results">
                    <div className="result-card green-card">
                      <div className="result-header">Your human life value is</div>
                      <div className="result-value">₹ {hlvResult.toLocaleString('en-IN')}</div>
                    </div>
                    <p className="disclaimer">*Your human life value is calculated based on the information provided by you and is indicative.</p>
                  </div>
                </div>
              </div>
            )}

            {selectedCategory === 'Insurance' && activeCalculator === 'BMI Calculator' && (
              <div className="bmi-calculator-wrapper">
                <div className="calculator-wrapper-flex">
                  <div className="calculator-inputs">
                    {/* Height Input */}
                    <div className="input-group">
                      <div className="toggle-group">
                        <button className={`toggle-btn ${heightUnit === 'ft' ? 'active' : ''}`} onClick={() => setHeightUnit('ft')}>Feet</button>
                        <button className={`toggle-btn ${heightUnit === 'cm' ? 'active' : ''}`} onClick={() => setHeightUnit('cm')}>CM</button>
                      </div>
                      <label className="input-label">HEIGHT</label>
                      <div className="display-value">{formatHeight()}</div>
                      <input
                        type="range"
                        value={height}
                        onChange={(e) => setHeight(Number(e.target.value))}
                        min="100"
                        max="250"
                        className="slider"
                      />
                    </div>

                    {/* Weight Input */}
                    <div className="input-group">
                      <div className="toggle-group">
                        <button className={`toggle-btn ${weightUnit === 'kg' ? 'active' : ''}`} onClick={() => setWeightUnit('kg')}>KG</button>
                        <button className={`toggle-btn ${weightUnit === 'lbs' ? 'active' : ''}`} onClick={() => setWeightUnit('lbs')}>Pounds</button>
                      </div>
                      <label className="input-label">WEIGHT</label>
                      <div className="display-value">{formatWeight()}</div>
                      <input
                        type="range"
                        value={weight}
                        onChange={(e) => setWeight(Number(e.target.value))}
                        min="30"
                        max="150"
                        className="slider"
                      />
                    </div>
                  </div>

                  <div className="calculator-results bmi-results-advanced">
                    {/* SVG Gauge */}
                    <div className="bmi-gauge">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Segments */}
                        <path d="M 20 100 A 80 80 0 0 1 60 30" fill="none" stroke="#90caf9" strokeWidth="25" />
                        <path d="M 60 30 A 80 80 0 0 1 100 20" fill="none" stroke="#8bc34a" strokeWidth="25" />
                        <path d="M 100 20 A 80 80 0 0 1 140 30" fill="none" stroke="#ff9800" strokeWidth="25" />
                        <path d="M 140 30 A 80 80 0 0 1 180 100" fill="none" stroke="#f44336" strokeWidth="25" />

                        {/* Labels - Positioned inside the colored segments */}
                        <text x="40" y="65" fontSize="8" fill="#000" fontWeight="700" transform="rotate(-60 40,65)" textAnchor="middle">UNDERWEIGHT</text>
                        <text x="80" y="28" fontSize="8" fill="#000" fontWeight="700" transform="rotate(-20 80,28)" textAnchor="middle">NORMAL</text>
                        <text x="120" y="28" fontSize="8" fill="#000" fontWeight="700" transform="rotate(20 120,28)" textAnchor="middle">OVERWEIGHT</text>
                        <text x="160" y="65" fontSize="8" fill="#000" fontWeight="700" transform="rotate(60 160,65)" textAnchor="middle">OBESE</text>

                        {/* Needle */}
                        <g className="needle" style={{ transform: `rotate(${(Math.min(40, Math.max(15, bmiDetails.bmi)) - 15) * (180 / 25)}deg)`, transformOrigin: '100px 100px' }}>
                          <line x1="100" y1="100" x2="20" y2="100" stroke="#333" strokeWidth="4" strokeLinecap="round" />
                          <circle cx="100" cy="100" r="6" fill="#333" />
                        </g>
                      </svg>
                    </div>

                    <div className="bmi-main-display">
                      <span className="bmi-label-text">Your BMI is</span>
                      <div className="bmi-value-row">
                        <span className="bmi-number" style={{ color: bmiDetails.color }}>{bmiDetails.bmi} Kg/M2</span>
                        <span className="bmi-category-text">{bmiDetails.category}</span>
                      </div>
                    </div>

                    <div className="bmi-details-card">
                      <ul className="details-list">
                        <li>Healthy BMI Range: 18.5 Kg/M2 - 25 Kg/M2</li>
                        <li>Healthy Weight For The Height: {bmiDetails.minHealthyWeight} Kgs - {bmiDetails.maxHealthyWeight} Kgs</li>
                        <li>{bmiDetails.weightChangeMsg}</li>
                        <li>BMI Prime: {bmiDetails.bmiPrime}</li>
                        <li>Ponderal Index: {bmiDetails.ponderalIndex} Kg/M3</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ABCCalculators;
